import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import { createTableFiltersStore } from "./createTableFiltersStore";

export interface ResourceActionListFiltersState extends Record<string, unknown> {
  /**
   * A GUID (action/resource/tenant/user id — never the VM's provider UUID, which is
   * display-only in the table and dialog) or a complete e-mail — never a substring of the
   * description or error text (2026-09-11 decision). Empty string means "no term".
   */
  search: string;

  // No action type or status filter: not needed on this screen (user decision, 2026-09-11).

  /** Empty string means "every tenant". */
  tenantId: string;

  /** Created-at range, ISO strings. Both ends or neither — the API ignores a half range. */
  dateRange: DateRangeValue;
}

/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

/**
 * The created-at range opens on the last six months rather than on "no filter", matching the
 * resource, user, tenant, payment and audit lists: this table grows a row per action across every
 * workspace, so an unbounded first page is the slowest query in the console and the least useful
 * answer.
 */
const defaults: ResourceActionListFiltersState = {
  search: "",
  tenantId: "",
  dateRange: lastMonthsRange(),
};

/**
 * Filter and paging state for the admin resource-actions list, cached in sessionStorage.
 *
 * Bump `version` in step with any change to the shape above — a stored filter the backend no
 * longer accepts comes back as a 400 on something the operator cannot see.
 */
export const useResourceActionFiltersStore =
  createTableFiltersStore<ResourceActionListFiltersState>({
    name: "admin.resourceActions.filters",
    version: 1,
    defaults,
    // 20, not the store's default 50: matches the audit log, the other cross-tenant action trail.
    defaultPageSize: 20,
    sanitize: (filters) => ({
      ...filters,

      // Exactly one end is not a range: the query applies createdFrom/createdTo together, so a
      // stored half would show as a filter chip that narrows nothing. An *empty* range is left
      // alone — it is the operator having deliberately cleared the six-month default to search
      // all time, and replacing it here would silently put the default back on every reload.
      dateRange: isHalfRange(filters.dateRange) ? defaults.dateRange : filters.dateRange,
    }),
  });
